import { type ComponentProps, type Ref, useMemo } from "react";
import { type VariantProps } from "class-variance-authority";
import { Slot, Slottable } from "@radix-ui/react-slot";

import { cn } from "../../lib/utils";
import { Spinner } from "../Spinner";
import { Tooltip } from "../Tooltip";
import { buttonVariants } from "./styles";
import { ButtonGroup } from "./ButtonGroup";

type ButtonStyleProps = Omit<VariantProps<typeof buttonVariants>, "iconOnly">;

export interface ButtonProps extends ComponentProps<"button">, ButtonStyleProps {
  ref?: Ref<HTMLButtonElement>;
  /**
   * Shape / emphasis of the button.
   * @default "solid"
   */
  variant?: "solid" | "outline" | "ghost" | "link";
  /**
   * Intent color, applied on top of the chosen `variant`.
   * @default "primary"
   */
  color?: "primary" | "secondary" | "destructive" | "muted";
  /**
   * Dimensional size.
   * @default "md"
   */
  size?: "xs" | "sm" | "md" | "lg";
  /**
   * Renders a square, icon-only button (no horizontal padding). Composes with `size`. Provide an
   * accessible name via `aria-label` or `hoverLabel` when the button has no visible text.
   * @default false
   */
  iconOnly?: boolean;
  /** Tooltip text shown on hover/focus; wraps the button in a `Tooltip`. */
  hoverLabel?: string;
  /** When true, shows a leading spinner and disables the button. */
  loading?: boolean;
  /** Merge props onto the child element (Radix `Slot`) instead of rendering a `<button>`. */
  asChild?: boolean;
}

/**
 * The primary interactive control. Three orthogonal axes — `variant` (shape), `color` (intent),
 * and `size` (dimension) — compose freely; `iconOnly` renders a square icon button.
 *
 * @example
 * ```tsx
 * <Button>Save</Button>                                  // solid primary, md
 * <Button color="destructive">Delete</Button>            // solid red
 * <Button variant="outline" color="destructive">Remove</Button>
 * <Button variant="ghost" size="sm" iconOnly hoverLabel="Edit"><Pencil /></Button>
 * <Button loading>Saving…</Button>
 * ```
 */
export const ButtonRoot = ({
  className,
  variant = "solid",
  color = "primary",
  size = "md",
  iconOnly = false,
  hoverLabel,
  loading,
  asChild,
  disabled,
  children,
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : "button";
  const resolvedProps = useMemo<typeof props>(
    () => (Comp === "button" ? { type: "button", ...props } : props),
    [Comp, props]
  );

  // Solid buttons have a filled colored background, so the spinner needs an on-color (light) tone;
  // outline/ghost/link buttons are transparent with colored text, so the spinner tracks the intent.
  // `secondary` and `muted` are the exceptions: even their solid fills are light surfaces, so a `white`
  // spinner would wash out — they always track their own (dark-ink) intent tone regardless of variant.
  const spinnerColor = color === "muted" || color === "secondary" ? color : variant === "solid" ? "white" : color;

  const buttonEl = (
    <Comp
      ref={ref}
      data-slot="button"
      data-loading={loading ? "true" : undefined}
      className={cn(
        buttonVariants({ variant, color, size, iconOnly }),
        hoverLabel && disabled && "disabled:pointer-events-auto",
        className
      )}
      {...resolvedProps}
      // An icon-only button has no visible text to name it, so `hoverLabel` doubles as its
      // accessible name. Buttons with visible text keep that text as their name — labelling them
      // with `hoverLabel` would hide the visible label from assistive tech.
      aria-label={props["aria-label"] ?? (iconOnly ? hoverLabel : undefined)}
      disabled={loading || disabled}
    >
      {!!loading && <Spinner size="xs" color={spinnerColor} />}
      <Slottable>{children}</Slottable>
    </Comp>
  );

  if (hoverLabel) {
    return (
      <Tooltip>
        <Tooltip.Trigger asChild>{buttonEl}</Tooltip.Trigger>
        <Tooltip.Content>{hoverLabel}</Tooltip.Content>
      </Tooltip>
    );
  }

  return buttonEl;
};

export const Button = Object.assign(ButtonRoot, { Group: ButtonGroup });
